import { ApiError } from '@/lib/api-errors';
import { api } from '@/lib/api';

import type { ReportExportColumn } from './format-report-export';

const PAGE_SIZE = 100;
const HARD_LIMIT = 10_000;

export interface ReportExportResult {
  reportKey: string;
  columns: ReportExportColumn[];
  omittedColumns: string[];
  rows: Record<string, unknown>[];
  totals?: Record<string, unknown>;
}

type ReportRunResponse = ReportExportResult & {
  page: { page: number; pageSize: number; totalItems: number; totalPages: number };
};

/** Loads every row for a report (capped), for spreadsheet and PDF exports. */
export const fetchReportForExport = async (input: {
  reportKey: string;
  dateFrom?: string;
  dateTo?: string;
  filters?: Record<string, string>;
}): Promise<ReportExportResult> => {
  const rows: Record<string, unknown>[] = [];
  let page = 1;
  let totalPages = 1;
  let columns: ReportExportColumn[] = [];
  let omittedColumns: string[] = [];
  let totals: Record<string, unknown> | undefined;
  let reportKey = input.reportKey;

  while (page <= totalPages && rows.length < HARD_LIMIT) {
    const result = await api.get<ReportRunResponse>(
      `/reports/${input.reportKey}/run`,
      {
        query: {
          page,
          pageSize: PAGE_SIZE,
          dateFrom: input.dateFrom,
          dateTo: input.dateTo,
          ...input.filters,
        },
      },
    );

    columns = result.columns;
    omittedColumns = result.omittedColumns;
    totals = result.totals;
    reportKey = result.reportKey;

    if (page === 1 && result.page.totalItems > HARD_LIMIT) {
      throw new ApiError(413, { error: { code: 'EXPORT_TOO_LARGE' } });
    }

    rows.push(...result.rows);
    totalPages = result.page.totalPages;
    if (result.rows.length === 0) break;
    page += 1;
  }

  if (rows.length >= HARD_LIMIT) {
    throw new ApiError(413, { error: { code: 'EXPORT_TOO_LARGE' } });
  }

  return {
    reportKey,
    columns,
    omittedColumns,
    rows,
    totals,
  };
};
